import React, { useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import {
  Home as HomeIcon,
  KingBed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as SqftIcon,
  Domain as TypeIcon,
} from '@mui/icons-material';

/**
 * PropertyInfoDisplay
 * ────────────────────────────────────────────────────────────────────────────
 * Renders the Zillow-style summary of a job's `propertyInfo` JSONB.
 *
 * One component, three variants:
 *   - photo="hero"      Full-width photo header (~140px) + chips below.
 *                       Used on AvailableJobCard for browsing.
 *   - photo="thumbnail" Small (56px) photo to the left of chips.
 *                       Used inside denser detail headers (JobExecutionScreen).
 *   - photo="none"      Chips only. Used on slim queue tiles (MaidJobCard).
 *
 * Renders nothing when there's no useful info AND no photo region is requested —
 * keeps empty `propertyInfo: {}` invisible instead of showing a stub.
 *
 * For "hero" the photo area always renders (real image or gray placeholder) so
 * cards in a list have consistent vertical rhythm even when MLS data is missing.
 */
const humanizeType = (type) => {
  if (!type) return null;
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatSqft = (n) => {
  if (n == null) return null;
  return Number(n).toLocaleString();
};

const PHOTO_SIZES = {
  hero:      { width: '100%', height: 140, iconSize: 48 },
  thumbnail: { width: 56,     height: 56,  iconSize: 22 },
};

const PropertyPhoto = ({ url, variant }) => {
  // Hooks must run on every render — keep this above any conditional return
  const [errored, setErrored] = useState(false);
  const size = PHOTO_SIZES[variant];
  if (!size) return null;

  // Real photo (and not broken)
  if (url && !errored) {
    return (
      <Box
        component="img"
        src={url}
        alt="Property"
        onError={() => setErrored(true)}
        sx={{
          width: size.width,
          height: size.height,
          objectFit: 'cover',
          borderRadius: variant === 'hero' ? 0 : 1,
          display: 'block',
          bgcolor: 'grey.100',
          flexShrink: 0,
        }}
      />
    );
  }

  // Placeholder
  return (
    <Box
      sx={{
        width: size.width,
        height: size.height,
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: variant === 'hero' ? 0 : 1,
        flexShrink: 0,
      }}
    >
      <HomeIcon sx={{ color: 'grey.400', fontSize: size.iconSize }} />
    </Box>
  );
};

const PropertyChips = ({ info }) => {
  const chips = [];
  if (info?.bedrooms != null) {
    chips.push({ icon: <BedIcon sx={{ fontSize: 14 }} />, label: `${info.bedrooms} bd` });
  }
  if (info?.bathrooms != null) {
    chips.push({ icon: <BathIcon sx={{ fontSize: 14 }} />, label: `${info.bathrooms} ba` });
  }
  if (info?.squareFootage != null) {
    chips.push({ icon: <SqftIcon sx={{ fontSize: 14 }} />, label: `${formatSqft(info.squareFootage)} sqft` });
  }
  const typeLabel = humanizeType(info?.propertyType);
  if (typeLabel) {
    chips.push({ icon: <TypeIcon sx={{ fontSize: 14 }} />, label: typeLabel });
  }

  if (chips.length === 0) return null;

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
      {chips.map((c, i) => (
        <Chip key={i} icon={c.icon} label={c.label} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

const PropertyInfoDisplay = ({ propertyInfo, photo = 'none' }) => {
  const info = propertyInfo || {};
  const hasFields =
    info.bedrooms != null ||
    info.bathrooms != null ||
    info.squareFootage != null ||
    !!info.propertyType;

  // Nothing to render
  if (!hasFields && photo === 'none') return null;

  // Hero: always render the photo area (image or placeholder); chips conditional
  if (photo === 'hero') {
    return (
      <Box>
        <PropertyPhoto url={info.photoUrl} variant="hero" />
        {hasFields && (
          <Box px={1.5} pt={1} pb={0.5}>
            <PropertyChips info={info} />
          </Box>
        )}
      </Box>
    );
  }

  // Thumbnail: only useful if there are chips or a photo URL
  if (photo === 'thumbnail') {
    if (!hasFields && !info.photoUrl) return null;
    return (
      <Box display="flex" alignItems="center" gap={1.5}>
        <PropertyPhoto url={info.photoUrl} variant="thumbnail" />
        <Box flex={1} minWidth={0}>
          <PropertyChips info={info} />
        </Box>
      </Box>
    );
  }

  // none: chips only
  return <PropertyChips info={info} />;
};

export default PropertyInfoDisplay;
